import { state } from '../state.js';
import { shareReport } from './reportShare.js';

const getFirstTopicDate = (group) => {
    if (!group.topicDays || group.topicDays.length === 0) return new Date(0);
    return new Date(Math.min(...group.topicDays.map(td => new Date(td.date + 'T00:00:00').getTime())));
};

const findChildCycles = (childId, levelId) => {
    const level = state.data.levels.find(l => l.id == levelId);
    if (!level) return [];

    const enrolled = level.groups.filter(g =>
        g.children.some(c => c.id == childId)
    );

    return enrolled.sort((a, b) => getFirstTopicDate(a) - getFirstTopicDate(b));
};

// Returns all topics for the level from the flat topics catalog, grouped by title.
// Each entry: { ...topic, ids: [id, id2, ...] } (all topic IDs sharing the same title)
const getLevelTopics = (levelId) => {
    const byTitle = new Map(); // title -> { topic, ids: Set }
    state.data.topics.filter(a => a.level_id == levelId && a.atrium_day == 1).sort((a, b) => a.order - b.order).forEach(topic => {
        if (!byTitle.has(topic.title)) {
            byTitle.set(topic.title, { topic, ids: new Set([topic.id]) });
        } else {
            byTitle.get(topic.title).ids.add(topic.id);
        }
    });
    return Array.from(byTitle.values())
        .map(({ topic, ids }) => ({ ...topic, ids: Array.from(ids) }));
};

// topicIds: array of all IDs that share this topic's title
const getAttendanceSymbol = (group, topicIds, childId) => {
    if (!group) return '<span class="ra-dash">—</span>';

    const topicDay = group.topicDays.find(td => td.canceled == 0 && topicIds.some(id => id == td.topic_id));

    if (!topicDay) return '<span class="ra-dash">—</span>';

    if (topicDay.canceled == 1) return '<span class="ra-dash">—</span>';

    const record = topicDay.attendance.find(a => a.child_id == childId);
    if (!record) return '<span class="ra-dash">—</span>';
    if (record.attended === 1) return '<i class="bi bi-check-lg ra-check"></i>';
    if (record.attended === 0) return '<span class="ra-absent">A</span>';
    if (record.attended === null) return '<span class="ra-pending">?</span>';
    return '<span class="ra-dash">—</span>';
};

const buildReportHTML = (child, cycles, combinedTopics) => {
    const slots = [cycles[0] || null, cycles[1] || null, cycles[2] || null];

    const avatar = child.photo_url
        ? `<img src="${child.photo_url}" alt="${child.first_name} ${child.last_name}">`
        : '<i class="bi bi-person-circle"></i>';

    const colHeaders = slots.map((group, i) => {
        return `<th class="report-col-cycle"><span class="report-cycle-label">C${i + 1}</span></th>`;
    }).join('');

    const groupCycle = slots.map((group, i) => {
        const title = group ? (state.getGroup(group.id)?.title || `Cycle ${i + 1}`) : ``;
        return `<span class="report-cycle-group">C${i + 1}: ${title}</span>`;
    }).join('');

    const rows = combinedTopics.map((topic, i) => {
        const cells = slots.map(group =>
            `<td class="report-col-symbol">${getAttendanceSymbol(group, topic.ids, child.id)}</td>`
        ).join('');
        return `<tr><td class="report-col-topic">${(i + 1)}) ${topic.title}</td>${cells}</tr>`;
    }).join('');

    return `
        <div class="report-hero">
            <div class="report-avatar">${avatar}</div>
            <h2 class="report-name">${child.first_name}<br />${child.last_name}</h2>
            <button class="report-share-btn report-share-btn--desktop" data-child-id="${child.id}" aria-label="Share report">
                <i class="bi bi-share"></i>
            </button>
        </div>
        <div class="repor-cycles">
            ${groupCycle}
        </div>
        <table class="report-table">
            <thead>
                <tr>
                    <th class="report-col-topic">Topic</th>
                    ${colHeaders}
                </tr>
            </thead>
            <tbody>${rows}</tbody>
        </table>
    `;
};

let activeChildId = null;

export const renderChildReport = (childId) => {
    activeChildId = childId;
    const child = state.getChild(childId);
    if (!child) return;

    const group = state.getGroup(state.currentGroup);
    const levelId = group?.level_id;

    const cycles = findChildCycles(childId, levelId);
    const levelTopics = getLevelTopics(levelId);
    const html = buildReportHTML(child, cycles, levelTopics);

    if (window.innerWidth < 768) {
        const content = document.getElementById('childReportContent');
        const modal = document.getElementById('childReportModal');
        if (content) content.innerHTML = html;
        if (modal) modal.classList.add('is-visible');
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        history.pushState({ modal: 'childReportModal' }, '', location.pathname);
    } else {
        const reportPanel = document.getElementById('childReport');
        const detailsColumn = document.querySelector('.details-column');
        if (reportPanel) reportPanel.innerHTML = html;
        detailsColumn?.classList.add('show-report');
    }
};

export const resetReportPanel = () => {
    document.getElementById('peopleGrid').innerHTML = '';
    document.getElementById('desktopTitle').innerText = 'Select a child to see the report';
    document.querySelector('.details-column')?.classList.remove('show-report');
};

export const initReport = () => {
    document.getElementById('childReportClose')?.addEventListener('click', (e) => {
        e.preventDefault();
        history.back();
    });

    // Mobile share button (static in DOM)
    document.getElementById('reportShareBtn')?.addEventListener('click', (e) => {
        shareReport(activeChildId, e.currentTarget);
    });

    // Desktop share button (rendered inside report HTML — use delegation)
    document.getElementById('childReport')?.addEventListener('click', (e) => {
        const btn = e.target.closest('.report-share-btn--desktop');
        if (btn) shareReport(activeChildId, btn);
    });
};
